import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import type { ISensemakerListSummary } from '@brushfire/types'
import {
  SENSEMAKER_LIST_PILL_GAP_PX,
  SENSEMAKER_LIST_PILLS_TO_GRID_GAP_PX,
} from '../lib/sensemaker-list-layout'
import { applyQuickStatFilter, type SensemakerListFilterState } from '../lib/sensemaker-list-filters'

const PILL_STYLES = {
  active: { bg: '#d1fae5', border: '#10b981', color: '#065f46' },
  deactivated: { bg: '#fae3ed', border: '#ff5959', color: '#ff5959' },
  expired: { bg: '#fef3c7', border: '#f59e0b', color: '#92400e' },
  near_token_limit: { bg: '#fef3c7', border: '#f59e0b', color: '#92400e' },
} as const

interface Props {
  summary: ISensemakerListSummary
  filters: SensemakerListFilterState
  onFiltersChange: (next: SensemakerListFilterState) => void
}

export function SensemakerQuickStatPills({ summary, filters, onFiltersChange }: Props) {
  const pills = [
    { key: 'active' as const, label: 'Active', count: summary.active },
    { key: 'deactivated' as const, label: 'Deactivated', count: summary.deactivated },
    { key: 'expired' as const, label: 'Expired', count: summary.expired },
    { key: 'near_token_limit' as const, label: 'Near Token Limit', count: summary.nearTokenLimit },
  ]

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: `${SENSEMAKER_LIST_PILL_GAP_PX}px`,
        mb: `${SENSEMAKER_LIST_PILLS_TO_GRID_GAP_PX}px`,
      }}
      data-testid="sensemaker-quick-stat-pills"
    >
      {pills.map((pill) => {
        const style = PILL_STYLES[pill.key]
        const isActive = filters.status === pill.key
        return (
          <Box
            key={pill.key}
            component="button"
            type="button"
            data-testid={`sensemaker-quick-stat-${pill.key}`}
            onClick={() => onFiltersChange(applyQuickStatFilter(filters, pill.key))}
            sx={{
              border: `1px solid ${style.border}`,
              bgcolor: style.bg,
              color: style.color,
              borderRadius: '100px',
              px: 1.25,
              py: 0.625,
              cursor: 'pointer',
              fontFamily: '"Inter", sans-serif',
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '16px',
              boxShadow: isActive ? `inset 0 0 0 1px ${style.border}` : 'none',
            }}
          >
            <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
              {pill.label}: {pill.count}
            </Typography>
          </Box>
        )
      })}
    </Box>
  )
}
